import classnames from 'classnames'
import styles from './ProductGalleryListView.module.scss'
import SanityImage from '@/components/SanityImage/SanityImage'
import Link from '@/components/Link/Link'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import useWindowResize from '@/hooks/use-window-resize'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import gsap from 'gsap'
import { PRODUCT_GALLERY_BREAKPOINTS } from '@/sections/ProductGallery/ProductGallery'
import useStore from '@/store'
import { getProductLinkObject, lerp } from '@/utils'
import useBreakpoint from '@/hooks/use-breakpoint'
import { useRouter } from 'next/router'
import { PRODUCTS_SLUG } from '@/data'

ScrollTrigger.config({
  ignoreMobileResize: true,
})

const ProductGalleryListView = forwardRef(({ className, products, pageIsLeaving, allowListRender }, ref) => {
  const $container = useRef(null)
  const resizeKey = useWindowResize()
  const $itemImageContainers = useRef([])
  const $items = useRef([])
  const [topMargin, setTopMargin] = useState(0)
  const productGalleryType = useStore(state => state.productGalleryType)
  const productGalleryActiveFilter = useStore(state => state.productGalleryActiveFilter)
  const setProductGalleryActiveProductData = useStore(state => state.setProductGalleryActiveProductData)
  const itemHeightsLerped = useRef([])
  const [activeHoverIndex, setActiveHoverIndex] = useState(-1)
  const [productClicked, setProductClicked] = useState(null)
  const setCursorState = useStore(state => state.setCursorState)
  const allowProductGalleryListHover = useStore(state => state.allowProductGalleryListHover)
  const setAllowProductGalleryListHover = useStore(state => state.setAllowProductGalleryListHover)
  const subscribeRef = useRef(null)
  const { isMobile } = useBreakpoint()
  const triggeredProductClickNavigation = useRef(false)
  const router = useRouter()
  const loaderAnimationComplete = useStore(state => state.loaderAnimationComplete)
  const [productGalleryTypeDebounced, setProductGalleryTypeDebounced] = useState(productGalleryType)
  const productGalleryTypeDebouncedTimeout = useRef(null)

  useEffect(() => {
    if (productGalleryTypeDebouncedTimeout.current) {
      clearTimeout(productGalleryTypeDebouncedTimeout.current)
    }

    productGalleryTypeDebouncedTimeout.current = setTimeout(() => {
      setProductGalleryTypeDebounced(productGalleryType)
    }, 100)
  }, [productGalleryType])

  useEffect(() => {
    setAllowProductGalleryListHover(productGalleryTypeDebounced === 'list')

    // eslint-disable-next-line
  }, [productGalleryTypeDebounced])

  // const setDebugMobileText = useStore(state => state.setDebugMobileText)
  const options = useRef({
    maxHeight: 0,
    minHeight: 0,
    margin: 0,
    centerY: 0,
  })
  const lerpValue = useRef(1)

  const animateLerpValue = (zeroLerp = false) => {
    const animateTo = zeroLerp ? 1 : 0.25

    gsap.killTweensOf(lerpValue)
    gsap.to(lerpValue, {
      current: animateTo,
      duration: 0.25,
      ease: 'power3.inOut',
    })
  }

  const getBasicOptions = () => {
    const maxHeight = window.innerHeight * 0.55
    const minHeight = window.innerHeight * 0.2
    const margin = window.innerHeight * 0.55
    const centerY = window.innerHeight * 0.5

    const config = {
      maxHeight: maxHeight,
      minHeight: minHeight,
      margin,
      centerY,
    }

    return { config }
  }

  useEffect(() => {
    options.current = getBasicOptions()
  }, [resizeKey])

  const getActiveIndexFromPercentScrolled = percent => {
    let activeIndex = gsap.utils.mapRange(0, 1, 0, $items.current.length - 1, percent)
    activeIndex = gsap.utils.snap(1, activeIndex)
    return activeIndex
  }

  const getOffsetByPercentScrolled = percent => {
    const { config } = options.current
    const activeIndex = getActiveIndexFromPercentScrolled(percent)

    $items.current.forEach(($el, i) => {
      const $imageContainer = $itemImageContainers.current[i]
      if (!$imageContainer) return

      if (i === activeIndex && products[i]) {
        setProductGalleryActiveProductData(products[i])
      }
      // gsap.set($imageContainer, {
      //   outline: i === activeIndex ? '2px solid red' : 'none',
      // })
    })

    const itemOffsetsByIndex = {}
    $items.current.forEach((_, i) => {
      if (i === 0) {
        itemOffsetsByIndex[i] = 0
      } else {
        const offsetFromTop = i * window.innerHeight - config.margin * i
        itemOffsetsByIndex[i] = offsetFromTop
      }
    })

    const offset = itemOffsetsByIndex[activeIndex]

    return offset
  }

  const getActiveProductDataByPercentScrolled = percent => {
    const activeIndex = getActiveIndexFromPercentScrolled(percent)
    $items.current.forEach((_, i) => {
      if (i === activeIndex && products[i]) {
        setProductGalleryActiveProductData({
          ...products[i],
          index: i,
        })
      }
    })
  }

  const calculateOffsetOnScroll = () => {
    const offsets = []
    $itemImageContainers.current.forEach(($el, i) => {
      if (!$el) return

      const boundingRect = $el.getBoundingClientRect()

      offsets[i] = {
        top: boundingRect.top,
        left: boundingRect.left,
        width: $el.offsetWidth,
        height: $el.offsetHeight,
      }
    })

    return offsets
  }

  const raf = () => {
    const centerY = window.innerHeight * 0.5
    const { config } = options.current
    setTopMargin(config.margin * -1)

    $items.current.forEach(($el, i) => {
      const $imageContainer = $itemImageContainers.current[i]
      if (!$imageContainer) return

      const centerOffset = $el.getBoundingClientRect().top + $el.offsetHeight / 2
      let normalized = 1 - Math.abs((centerOffset - centerY) / window.innerHeight)
      normalized = gsap.utils.mapRange(0.55, 1, 0, 1, normalized)
      normalized = gsap.utils.clamp(0, 1, normalized)
      const height = gsap.utils.mapRange(0, 1, config.minHeight, config.maxHeight, normalized)

      // if (i === 2) {
      //   setDebugMobileText(`
      //     ${i} height: ${Math.round(height)}<br/>
      //     ${i} width: ${Math.round($el.scrollWidth)}<br/>
      //   `)
      // }

      if (!itemHeightsLerped.current[i]) {
        itemHeightsLerped.current[i] = {
          target: height,
          current: height,
        }
      }

      itemHeightsLerped.current[i].target = height
      itemHeightsLerped.current[i].current = lerp(
        itemHeightsLerped.current[i].current,
        itemHeightsLerped.current[i].target,
        lerpValue.current,
        // 0.25,
        // 1,
      )

      // on mobile the flip animation can be a lil jarrying because
      // of lerping. So when not on this view, just change the height
      // immediately

      gsap.set($imageContainer, {
        height: itemHeightsLerped.current[i].current,
        // height: productGalleryType === 'grid' ? height : itemHeightsLerped.current[i].current,
        // height,
      })
    })
  }

  useEffect(() => {
    gsap.ticker.remove(raf)

    if (pageIsLeaving) return

    raf()

    if (productGalleryType === 'list') {
      gsap.ticker.add(raf)
    }

    return () => {
      gsap.ticker.remove(raf)
    }
  }, [resizeKey, productGalleryType, pageIsLeaving])

  const setActiveHoverBasedOnCursorPosition = cursorPosition => {
    // Get all images with the styles.image className
    const images = document.querySelectorAll(`.${styles.image}`)

    let foundContainerIndex = -1

    // Check if cursor is within bounds of any image
    images.forEach(img => {
      const rect = img.getBoundingClientRect()

      // Check if cursor coordinates are within the image bounds
      if (
        cursorPosition.x >= rect.left &&
        cursorPosition.x <= rect.right &&
        cursorPosition.y >= rect.top &&
        cursorPosition.y <= rect.bottom
      ) {
        // Find which container this image belongs to
        const containerIndex = $itemImageContainers.current.findIndex(container => {
          if (!container) return false
          return container.contains(img)
        })

        if (containerIndex !== -1) {
          foundContainerIndex = containerIndex
        }
      }
    })

    if (foundContainerIndex !== -1) {
      // Cursor is over an image in container at index foundContainerIndex
      // You can add your logic here for when cursor is over an image
      setActiveHoverIndex(foundContainerIndex)
      return foundContainerIndex
    } else {
      // Cursor not over any image
      setActiveHoverIndex(-1)
      return null
    }
  }

  // Subscribe to cursor position and detect when cursor is over img elements
  useEffect(() => {
    if (!loaderAnimationComplete) return
    if (pageIsLeaving) return
    if (!$container.current) return
    if (isMobile === null) return

    // Handle click events
    const handleClick = event => {
      // eslint-disable-next-line
      // console.log({ allowProductGalleryListHover, productGalleryTypeDebounced })

      if (!allowProductGalleryListHover || productGalleryTypeDebounced !== 'list') return
      const clickCoordinates = {
        x: event.clientX,
        y: event.clientY,
      }
      const activeIndex = setActiveHoverBasedOnCursorPosition(clickCoordinates)

      setProductClicked(activeIndex)

      // eslint-disable-next-line
      // console.log({ activeIndex })
    }

    // remove listeners
    if (subscribeRef.current) subscribeRef.current()
    $container.current.removeEventListener('click', handleClick)

    if (productGalleryTypeDebounced !== 'list') {
      return
    }

    // add click listener
    $container.current.addEventListener('click', handleClick)

    if (!isMobile) {
      subscribeRef.current = useStore.subscribe(
        state => [state.cursorPosition],
        value => {
          const cursorPosition = value[0]
          if (!cursorPosition?.x || !cursorPosition?.y) return
          setActiveHoverBasedOnCursorPosition(cursorPosition)
        },
      )
    }

    const container = $container.current

    return () => {
      if (subscribeRef.current) subscribeRef.current()
      container.removeEventListener('click', handleClick)
    }
  }, [pageIsLeaving, isMobile, loaderAnimationComplete, allowProductGalleryListHover, productGalleryTypeDebounced])

  useEffect(() => {
    const activeProduct = products.find((_, i) => i === activeHoverIndex)

    if (activeProduct && allowProductGalleryListHover) {
      setCursorState(activeProduct?.title)
    } else {
      setCursorState(null)
    }

    // eslint-disable-next-line
  }, [activeHoverIndex, allowProductGalleryListHover])

  useEffect(() => {
    // console.log({ productClicked })
    if (typeof productClicked === 'number' && !triggeredProductClickNavigation.current) {
      triggeredProductClickNavigation.current = true
      const product = products[productClicked]
      const path = `/${PRODUCTS_SLUG}/${product.slug.current}`
      // eslint-disable-next-line
      // console.log({ product, path })
      if (product) {
        setCursorState(null)
        router.push(
          {
            pathname: path,
          },
          undefined,
          { scroll: false, shallow: false },
        )
      }
    }
    // eslint-disable-next-line
  }, [productClicked, products, router])

  useImperativeHandle(ref, () => ({
    getElement: () => {
      return $container
    },
    getOffsetByPercentScrolled,
    calculateOffsetOnScroll,
    raf,
    getActiveProductDataByPercentScrolled,
    animateLerpValue,
  }))

  if (!allowListRender) return null

  return (
    <div
      className={classnames(styles.ProductGalleryListView, className, {
        [styles.isHoveringOverItem]: activeHoverIndex !== -1,
      })}
      ref={$container}
      style={{
        '--top-margin': `${topMargin}px`,
      }}
    >
      <ul className={styles.list}>
        {products.map((product, i) => (
          <li
            key={i}
            data-orientation={product.imageOrientation}
            className={classnames(styles.item, {
              [styles.notInFilter]:
                productGalleryActiveFilter && productGalleryActiveFilter !== product.productCategory.title,
              [styles.active]: activeHoverIndex === i,
            })}
            ref={ref => {
              $items.current[i] = ref
            }}
          >
            <Link
              ariaHidden={productGalleryType !== 'list'}
              ariaLabel={`Learn more about ${product.title}`}
              link={getProductLinkObject(product.slug.current)}
              className={styles.itemLink}
            ></Link>
            <div className={styles.itemImageContainerOuter}>
              <div
                className={styles.itemImageContainer}
                data-index={i}
                data-box-type="list"
                ref={ref => {
                  $itemImageContainers.current[i] = ref
                }}
              >
                <SanityImage
                  image={product.image}
                  className={styles.image}
                  breakpoints={PRODUCT_GALLERY_BREAKPOINTS}
                  disableLoadedOpacity
                  priority
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
})

ProductGalleryListView.displayName = 'ProductGalleryListView'

export default ProductGalleryListView
